import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { IconLoader2, IconHomePlus } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils/cn";
import { createProperty, PropertyActionError } from "../api";

interface CreatePropertyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPES = ["apartment", "house", "land", "commercial"] as const;
const OPERATIONS = ["sale", "rent", "both"] as const;

/**
 * Cadastro do imóvel da casa (hub#689). Pede só o essencial: fotos, vídeos, vitrine e
 * vistoria seguem no CRM, no imóvel já criado — replicar o formulário completo do corretor
 * aqui seria manter dois formulários em drift.
 */
export function CreatePropertyModal({ open, onOpenChange }: CreatePropertyModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().min(3, t("properties.form.titleRequired")),
        type: z.enum(TYPES),
        operation: z.enum(OPERATIONS),
        salePrice: z.coerce.number().optional(),
        rentPrice: z.coerce.number().optional(),
        street: z.string().min(2, t("properties.form.required")),
        number: z.string().min(1, t("properties.form.required")),
        neighborhood: z.string().min(2, t("properties.form.required")),
        city: z.string().min(2, t("properties.form.required")),
        uf: z.string().length(2, t("properties.form.ufInvalid")),
        bedrooms: z.coerce.number().optional(),
        bathrooms: z.coerce.number().optional(),
        parkingSpots: z.coerce.number().optional(),
        builtArea: z.coerce.number().optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { type: "apartment", operation: "sale" },
  });

  const operation = watch("operation");

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      createProperty({
        title: values.title,
        type: values.type,
        category: "residential",
        operation: values.operation,
        salePrice: values.operation === "rent" ? null : (values.salePrice ?? null),
        rentPrice: values.operation === "sale" ? null : (values.rentPrice ?? null),
        address: {
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          uf: values.uf.toUpperCase(),
        },
        bedrooms: values.bedrooms ?? null,
        bathrooms: values.bathrooms ?? null,
        parkingSpots: values.parkingSpots ?? null,
        builtArea: values.builtArea ?? null,
      }),
    onSuccess: () => {
      toast.success(t("properties.toast.created"));
      reset();
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["agency-properties"] });
    },
    onError: (error: Error) => {
      toast.error(
        error instanceof PropertyActionError
          ? t([`properties.errors.${error.code}`, "properties.errors.default"])
          : t("properties.errors.default"),
      );
    },
  });

  const field =
    "w-full rounded-xl border border-border bg-gray-50 px-4 py-2.5 text-sm outline-none transition-all focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/10";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t("properties.form.title")}</DialogTitle>
          <DialogDescription>{t("properties.form.desc")}</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((values) => create.mutate(values))}
          className="space-y-4 py-2"
        >
          <div className="space-y-1.5">
            <label htmlFor="title" className="text-sm font-bold text-ink">
              {t("properties.form.titleLabel")}
            </label>
            <input id="title" className={field} {...register("title")} />
            {errors.title && (
              <p className="text-xs font-bold text-red-500">{errors.title.message}</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="type" className="text-sm font-bold text-ink">
                {t("properties.form.typeLabel")}
              </label>
              <select id="type" className={field} {...register("type")}>
                {TYPES.map((value) => (
                  <option key={value} value={value}>
                    {t(`properties.types.${value}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="operation" className="text-sm font-bold text-ink">
                {t("properties.form.operationLabel")}
              </label>
              <select id="operation" className={field} {...register("operation")}>
                {OPERATIONS.map((value) => (
                  <option key={value} value={value}>
                    {t(`properties.operations.${value}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {operation !== "rent" && (
              <div className="space-y-1.5">
                <label htmlFor="salePrice" className="text-sm font-bold text-ink">
                  {t("properties.form.salePrice")}
                </label>
                <input id="salePrice" type="number" step="1000" className={field} {...register("salePrice")} />
              </div>
            )}
            {operation !== "sale" && (
              <div className="space-y-1.5">
                <label htmlFor="rentPrice" className="text-sm font-bold text-ink">
                  {t("properties.form.rentPrice")}
                </label>
                <input id="rentPrice" type="number" step="100" className={field} {...register("rentPrice")} />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
            <div className="space-y-1.5">
              <label htmlFor="street" className="text-sm font-bold text-ink">
                {t("properties.form.street")}
              </label>
              <input id="street" className={field} {...register("street")} />
              {errors.street && (
                <p className="text-xs font-bold text-red-500">{errors.street.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label htmlFor="number" className="text-sm font-bold text-ink">
                {t("properties.form.number")}
              </label>
              <input id="number" className={field} {...register("number")} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-[2fr_2fr_1fr]">
            <div className="space-y-1.5">
              <label htmlFor="neighborhood" className="text-sm font-bold text-ink">
                {t("properties.form.neighborhood")}
              </label>
              <input id="neighborhood" className={field} {...register("neighborhood")} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="city" className="text-sm font-bold text-ink">
                {t("properties.form.city")}
              </label>
              <input id="city" className={field} {...register("city")} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="uf" className="text-sm font-bold text-ink">
                {t("properties.form.uf")}
              </label>
              <input id="uf" maxLength={2} className={cn(field, "uppercase")} {...register("uf")} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <label htmlFor="bedrooms" className="text-sm font-bold text-ink">
                {t("properties.form.bedrooms")}
              </label>
              <input id="bedrooms" type="number" min={0} className={field} {...register("bedrooms")} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="bathrooms" className="text-sm font-bold text-ink">
                {t("properties.form.bathrooms")}
              </label>
              <input id="bathrooms" type="number" min={0} className={field} {...register("bathrooms")} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="parkingSpots" className="text-sm font-bold text-ink">
                {t("properties.form.parking")}
              </label>
              <input id="parkingSpots" type="number" min={0} className={field} {...register("parkingSpots")} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="builtArea" className="text-sm font-bold text-ink">
                {t("properties.form.area")}
              </label>
              <input id="builtArea" type="number" min={0} className={field} {...register("builtArea")} />
            </div>
          </div>

          <p className="rounded-xl bg-gray-50 px-4 py-3 text-xs text-ink-muted">
            {t("properties.form.mediaHint")}
          </p>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-xl px-4 py-2 text-sm font-bold text-ink-muted transition-colors hover:bg-gray-100"
            >
              {t("properties.form.cancel")}
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className={cn(
                "inline-flex items-center gap-2 rounded-xl px-6 py-2 text-sm font-black uppercase tracking-wider text-white shadow-soft transition-all",
                "bg-brand-grad hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50",
              )}
            >
              {create.isPending ? (
                <IconLoader2 size={18} className="animate-spin" />
              ) : (
                <IconHomePlus size={18} />
              )}
              {t("properties.form.submit")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
